import { Component, Suspense, useMemo, useState } from 'react';
import { lazyWithReload } from '../utils/lazyLoad';

/*
 * The way into the `walkthroughs` chunk from the parent chunks: a lazy card host
 * for the step detail and a lazy screen for the Handbook route. Static in the
 * parent, lazy behind it, so a step with no `walkthroughs[]` costs nothing and a
 * chunk that fails to arrive costs a quiet line and a Retry rather than the
 * workspace's own fault screen (plan R11).
 */

const importIndex = () => import(/* webpackChunkName: "walkthroughs" */ './index');

/** The Handbook route element, one per app. */
export const HandbookRoute = lazyWithReload(() => importIndex().then((module) => ({ default: module.HandbookScreen })));

/** The walkthrough page, `handbook/:walkthroughId`, in the same chunk. */
export const WalkthroughRoute = lazyWithReload(() => importIndex().then((module) => ({ default: module.WalkthroughPage })));

/** A "How we work here" page, `handbook/pages/:pageId`, in the same chunk (plan §15.4). */
export const HandbookPageRoute = lazyWithReload(() => importIndex().then((module) => ({ default: module.HandbookPage })));

/** Catches a chunk that did not arrive, and says so where the cards would have been. */
class QuietBoundary extends Component {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error) {
        if (typeof console !== 'undefined') console.warn('walkthrough cards did not load', error && error.message);
    }

    render() {
        if (!this.state.failed) return this.props.children;
        return (
            <div className="ws-wk-cards" data-testid="ws-wk-cards-failed">
                <p className="ws-detail-quiet">
                    {'The colleagues on this step did not load. '}
                    <button type="button" className="ws-btn" onClick={this.props.onRetry}>Retry</button>
                </p>
            </div>
        );
    }
}

/**
 * The cards beside a brief, loaded when the step carries any. A retry makes a
 * fresh lazy component, because `React.lazy` remembers a failed import for good.
 */
export const WalkthroughCardsLoader = (props) => {
    const [attempt, setAttempt] = useState(0);
    const Cards = useMemo(
        () => lazyWithReload(() => importIndex().then((module) => ({ default: module.WalkthroughCards }))),
        // A new component per attempt is the point.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [attempt]
    );
    return (
        <QuietBoundary key={attempt} onRetry={() => setAttempt((round) => round + 1)}>
            <Suspense fallback={null}>
                <Cards {...props} />
            </Suspense>
        </QuietBoundary>
    );
};
